import { useMemo } from "react";
import { useCustom } from "@refinedev/core";
import { SelectField } from "@fawkes/ui";

// BTW is verplicht op elke prijs en elk tarief: 0% mag, maar het moet gekozen zijn. Een tarief zonder
// BTW gaf de asiel-tariefmotor geen percentage, en de factuur viel dan stilletjes terug op 21% — ook
// waar 9% of 0% hoorde. Dit bestand is de ene plek waar de tariefschermen die keuze vandaan halen, zodat
// ze er allemaal hetzelfde uitzien en zich hetzelfde gedragen (het productscherm deed het al zo).

/** Eén BTW-code zoals `GET /api/producten/opties` hem levert. */
export interface BtwCode {
  id: number;
  /** "code | percentage%", zoals het productscherm hem toont. */
  label: string;
  /** Het percentage als getal; de factuurregel slaat een percentage op, geen code. */
  percentage: number | null;
}

/**
 * De BTW-codes uit dezelfde feed die het productscherm gebruikt. Geen aparte tarief-feed: twee lijsten
 * die uit elkaar lopen is erger dan er een lenen. `enabled` laat de kiezer pas laden als er echt een
 * keuze nodig is; retry:false zodat een onbereikbare feed het scherm niet blokkeert.
 */
export function useBtwCodes(enabled = true): BtwCode[] {
  const { data } = useCustom<{ btwCodes?: BtwCode[] }>({
    url: "producten/opties",
    method: "get",
    queryOptions: { enabled, retry: false },
  });
  return useMemo(() => {
    const codes = data?.data?.btwCodes;
    return Array.isArray(codes) ? codes : [];
  }, [data]);
}

/**
 * De BTW van de versie die nu loopt, uit een tariefhistorie (nieuwste eerst). Valt terug op de jongste
 * versie die er een heeft. Daarmee vult een prijswijziging de BTW alvast in: wie alleen het bedrag
 * aanpast, hoeft de BTW niet opnieuw op te zoeken. Leeg als het tarief nog nergens BTW heeft.
 */
export function huidigeBtwUitHistorie(
  historie: { btwCodeId: number | null; status?: string }[],
): string {
  const actief = historie.find((v) => v.status === "ACTIEF" && v.btwCodeId != null);
  const eerste = actief ?? historie.find((v) => v.btwCodeId != null);
  return eerste?.btwCodeId != null ? String(eerste.btwCodeId) : "";
}

/**
 * De BTW-percentages uit dezelfde codefeed, uniek en van hoog naar laag (21, 9, 0). Een kostenregel in
 * een wizard slaat een percentage op, geen code, dus de keuzelijst daar toont alleen percentages.
 */
export function useBtwPercentages(enabled = true): number[] {
  const codes = useBtwCodes(enabled);
  return useMemo(
    () => [...new Set(codes.map((c) => c.percentage).filter((p): p is number => p != null))].sort((a, b) => b - a),
    [codes],
  );
}

/**
 * De BTW op één kostenregel van een wizard: een smalle keuzelijst naast het bedrag. Uit de catalogus of de
 * tariefmotor komt het percentage al mee; een handmatige of standaardregel begint leeg. Zonder die keuze
 * viel de conceptfactuur stil terug op 21%, ook waar 9% of 0% hoorde.
 */
export function KostenregelBtw({
  value,
  onChange,
  percentages,
  missing,
  t,
}: {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  percentages: number[];
  missing: boolean;
  t: (key: string) => string;
}) {
  // Een percentage dat (nog) niet in de feed staat blijft zichtbaar gekozen, net als bij BtwKeuze.
  const opties = value != null && !percentages.includes(value) ? [...percentages, value] : percentages;
  return (
    <select
      className="fui-field__input fawkes-inboeken__cost-btw"
      aria-label={t("btw.label")}
      aria-invalid={missing || undefined}
      value={value == null ? "" : String(value)}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    >
      <option value="">{t("btw.kolom")}</option>
      {opties.map((p) => (
        <option key={p} value={String(p)}>{`${p}%`}</option>
      ))}
    </select>
  );
}

/** Staat er een regel met een bedrag maar zonder BTW? Die houdt de kostenstap tegen. */
export function kostenregelZonderBtw(
  regels: { amount: string; btw?: number | null }[],
  bedrag: (tekst: string) => number | null | undefined,
): boolean {
  return regels.some((r) => bedrag(r.amount) != null && r.btw == null);
}

/** Het percentage van een BTW-code voor weergave ("21%"), of "—" als de code onbekend is. */
export function btwWeergave(codes: BtwCode[], btwCodeId: number | null | undefined): string {
  if (btwCodeId == null) return "—";
  const code = codes.find((c) => c.id === btwCodeId);
  return code?.percentage != null ? `${code.percentage}%` : code?.label ?? "—";
}

/**
 * Het verplichte BTW-veld. Begint op "Kies een BTW-tarief" in plaats van op een percentage: een
 * voorgekozen 21% is precies de stille aanname waar dit veld tegen bestaat. Het scherm blokkeert zelf het
 * opslaan zolang er niets gekozen is en zet dan `missing` aan; de melding staat direct onder het veld.
 */
export function BtwKeuze({
  value,
  onChange,
  codes,
  missing,
  t,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Alleen id + label zijn nodig; schermen die de opties-feed al zelf laden geven die gewoon door. */
  codes: Pick<BtwCode, "id" | "label">[];
  missing: boolean;
  t: (key: string) => string;
  /** Een eigen label, bijvoorbeeld in een rij met meerdere prijsvelden. Standaard "BTW-tarief". */
  label?: string;
}) {
  // Een voorgevulde code die (nog) niet in de lijst staat — de feed laadt nog — blijft zichtbaar
  // gekozen in plaats van ongemerkt op "Kies…" terug te springen.
  const opties = codes.some((c) => String(c.id) === value) || value === ""
    ? codes.map((c) => ({ value: String(c.id), label: c.label }))
    : [...codes.map((c) => ({ value: String(c.id), label: c.label })), { value, label: value }];
  return (
    <div className="btw-keuze">
      <SelectField
        label={label ?? t("btw.label")}
        value={value}
        required
        aria-required="true"
        aria-invalid={missing || undefined}
        onChange={(e) => onChange(e.target.value)}
        options={[{ value: "", label: t("btw.kies") }, ...opties]}
      />
      {missing && (
        <div className="asiel-tarief__field-error" role="alert">
          {t("btw.verplicht")}
        </div>
      )}
    </div>
  );
}
